import type { PnlHierarchyMember } from 'src/components/star-grid/starGridTypes';
// How a picked node becomes line rows: the node alone (a consolidation row, no children), the node
// plus its immediate children, or only its leaf descendants.
export type StudioLineAddMode = 'self' | 'children' | 'leaves';
// Turn a member-picker selection into the rows to add. The picker cascades a checked parent to all
// its descendants, so the operable set is the selection's ROOTS (nodes with no selected ancestor) —
// i.e. what the operator actually ticked. Each root is then expanded per mode. Order follows the
// picker's hierarchy-ordered ids; a multi-parent member is emitted once.
export const expandLineSelection = (members: readonly PnlHierarchyMember[], selectedIds: readonly string[], mode: StudioLineAddMode): string[] => {
  const selected = new Set(selectedIds);
  const byId = new Map<string, PnlHierarchyMember>();
  const parentOf = new Map<string, string | undefined>();
  const walk = (node: PnlHierarchyMember, parent?: string): void => {
    byId.set(node.id, node);
    parentOf.set(node.id, parent);
    node.children?.forEach((child) => walk(child, node.id));
  };
  members.forEach((member) => walk(member));
  const hasSelectedAncestor = (id: string): boolean => {
    for (let ancestor = parentOf.get(id); ancestor; ancestor = parentOf.get(ancestor)) if (selected.has(ancestor)) return true;
    return false;
  };
  const leafDescendants = (id: string): string[] => {
    const node = byId.get(id);
    if (!node || node.is_leaf || !node.children?.length) return [id];
    return node.children.flatMap((child) => leafDescendants(child.id));
  };
  const roots = selectedIds.filter((id) => !hasSelectedAncestor(id));
  const out: string[] = [];
  for (const id of roots) {
    if (mode === 'self') out.push(id);
    else if (mode === 'children') out.push(id, ...(byId.get(id)?.children ?? []).map((child) => child.id));
    else out.push(...leafDescendants(id));
  }
  return [...new Set(out)];
};
